/* eslint-disable no-console */
import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import React, { useEffect, useState } from 'react';
import { companyIdAtom } from 'views/state';

import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
  policyIdFilterAtom,
} from './state';

export const useFilterConfig = (): FilterConfig => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  const companyId = useAtomValue(companyIdAtom);
  const [policyId, setPolicyId] = useAtom(policyIdFilterAtom);
  const [osOptions, setOsOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // initial filter values from query string
  useEffect(() => {
    setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {
      const params = new URLSearchParams(window.location.search);
      const entries = Array.from(params.entries());

      for (const [key, value] of entries) {
        if (key === 'policy_id') {
          setPolicyId(value === 'all' ? 'all' : parseInt(value));
        } else if (key.startsWith('dt_filter__')) {
          const filter = key.replace('dt_filter__', '');
          const [filterKey, rangeType] = filter.split('__');
          const fromValue = rangeType === 'from' ? parseInt(value) : null;
          const toValue = rangeType === 'to' ? parseInt(value) : null;
          const filterValue = queryFilters[filterKey]
            ? queryFilters[filterKey]
            : { from: fromValue, to: toValue };
          queryFilters[filterKey] = [filterValue];
        } else {
          queryFilters[key] = value.split(',');
        }
      }
    };

    initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          setFilterValues(queryFilters);
        }
      })
      .finally(() => {
        setQuerystringFiltersInitialized(true);
      });

    return () => {
      setQuerystringFiltersInitialized(false);
    };
  }, []);

  useEffect(() => {
    const fetchOperatingSystems = async () => {
      try {
        if (companyId && companyId !== -1) {
          const params = {
            company_id: companyId,
            // fetch all OS if policyId is 'all', otherwise fetch OS for the selected policy
            ...(policyId && policyId !== 'all' && { policy_id: policyId }),
          };

          const responseData = await callApi('pcm/assets-os/', 'GET', params);
          const osOptions =
            responseData?.data.map((item) => ({
              label: item,
              value: item,
            })) || [];
          setOsOptions(osOptions);
          return;
        }
      } catch (error) {
        console.error(error);
        setOsOptions([]);
      }
    };
    fetchOperatingSystems();
  }, [policyId, companyId]);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: ['os', 'scan_status', 'findings_status'],
      leftPanelFilters: ['dateRangeFilters', 'ip'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'os',
          label: 'Operating System',
          placeholder: 'Select OS',
          tooltip: (
            <div>
              <p>Filter hosts by Operating Systems (multi select).</p>
              <p>
                <strong>Note:</strong> The Operating Systems options will
                populate based on the selected policy filter.
              </p>
            </div>
          ),
          iconClass: 'fas fa-desktop',
          defaultSelectedValues: [''],
          enabled: osOptions.length > 0,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...osOptions,
          ],
          renderStyle:
            osOptions.length > 10
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
        new InputFilterConfig({
          id: 'ip',
          label: 'IP Address',
          tooltip: 'Filter by Host IP Address.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'text',
            placeholder: 'Host IP Address',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
        }),
        new MultiSelectFilterConfig({
          id: 'scan_status',
          label: 'Scan Status',
          tooltip:
            'Filter by scan status. Identify if some hosts are not getting scanned.',
          iconClass: 'fas fa-exclamation-circle',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Scanned', value: '1', deselectOthers: true },
            { label: 'Not Scanned', value: '0', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'findings_status',
          label: 'Findings Status',
          tooltip:
            'Filter by findings status. Identify hosts where findings are not discovered.',
          iconClass: 'fas fa-exclamation-triangle',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Discovered', value: '1', deselectOthers: true },
            { label: 'Not Discovered', value: '0', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'ca_modified',
              label: 'Modified Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_checked_in',
              label: 'Checked In Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_compliance_scan',
              label: 'Scan Date',
              labelWidth: '115px',
            }),
          ],
        }),
      ],
    });

    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, osOptions]);

  return config;
};
